import styled, { keyframes, css } from 'styled-components'
import * as SheetPrimitive from '@radix-ui/react-dialog'
import { XIcon } from 'lucide-react'
import { getFontStyle } from '@diversioteam/diversio-ds'

// Animation keyframes
const fadeIn = keyframes`
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
`

const fadeOut = keyframes`
    from {
        opacity: 1;
    }
    to {
        opacity: 0;
    }
`

const slideInFromRight = keyframes`
    from {
        transform: translateX(100%);
    }
    to {
        transform: translateX(0);
    }
`

const slideOutToRight = keyframes`
    from {
        transform: translateX(0);
    }
    to {
        transform: translateX(100%);
    }
`

const slideInFromLeft = keyframes`
    from {
        transform: translateX(-100%);
    }
    to {
        transform: translateX(0);
    }
`

const slideOutToLeft = keyframes`
    from {
        transform: translateX(0);
    }
    to {
        transform: translateX(-100%);
    }
`

const slideInFromTop = keyframes`
    from {
        transform: translateY(-100%);
    }
    to {
        transform: translateY(0);
    }
`

const slideOutToTop = keyframes`
    from {
        transform: translateY(0);
    }
    to {
        transform: translateY(-100%);
    }
`

const slideInFromBottom = keyframes`
    from {
        transform: translateY(100%);
    }
    to {
        transform: translateY(0);
    }
`

const slideOutToBottom = keyframes`
    from {
        transform: translateY(0);
    }
    to {
        transform: translateY(100%);
    }
`

export const StyledOverlay = styled(SheetPrimitive.Overlay)`
    /* data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50 */
    position: fixed;
    inset: 0;
    z-index: 50;
    background-color: rgba(0, 0, 0, 0.5);

    &[data-state='open'] {
        animation: ${fadeIn} 150ms cubic-bezier(0.16, 1, 0.3, 1);
    }

    &[data-state='closed'] {
        animation: ${fadeOut} 150ms cubic-bezier(0.16, 1, 0.3, 1);
    }
`

export const StyledContent = styled(SheetPrimitive.Content)<{ $side?: 'top' | 'right' | 'bottom' | 'left' }>`
    /* bg-background data-[state=open]:animate-in data-[state=closed]:animate-out fixed z-50 flex flex-col gap-4 shadow-lg transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500 */
    background-color: var(--background, #ffffff);
    position: fixed;
    z-index: 50;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    box-shadow:
        0 10px 15px -3px rgba(0, 0, 0, 0.1),
        0 4px 6px -2px rgba(0, 0, 0, 0.05);
    transition: all ease-in-out;

    /* Allow className overrides for background and spacing */
    &.bg-sidebar {
        background-color: #5e4ae3; /* Primary purple color */
    }

    &.p-0 {
        padding: 0;
        gap: 0; /* Remove gap when padding is 0 */
    }

    &.w-64 {
        width: 16rem; /* 256px */
    }

    &.border-sidebar-border {
        border-color: rgba(255, 255, 255, 0.2); /* White with opacity for sidebar borders */
    }

    /* Right side (default) */
    ${(props) =>
        (!props.$side || props.$side === 'right') &&
        css`
            /* inset-y-0 right-0 h-full w-3/4 border-l sm:max-w-sm */
            top: 0;
            bottom: 0;
            right: 0;
            height: 100%;
            width: 75%;
            border-left: 1px solid var(--border, #e0e0e0);

            @media (min-width: 640px) {
                max-width: 24rem; /* sm:max-w-sm */
            }

            &[data-state='open'] {
                animation: ${slideInFromRight} 500ms cubic-bezier(0.16, 1, 0.3, 1);
            }

            &[data-state='closed'] {
                animation: ${slideOutToRight} 300ms cubic-bezier(0.16, 1, 0.3, 1);
            }
        `}

    /* Left side */
    ${(props) =>
        props.$side === 'left' &&
        css`
            /* inset-y-0 left-0 h-full w-3/4 border-r sm:max-w-sm */
            top: 0;
            bottom: 0;
            left: 0;
            height: 100%;
            width: 75%;
            border-right: 1px solid var(--border, #e0e0e0);

            @media (min-width: 640px) {
                max-width: 24rem; /* sm:max-w-sm */
            }

            &[data-state='open'] {
                animation: ${slideInFromLeft} 500ms cubic-bezier(0.16, 1, 0.3, 1);
            }

            &[data-state='closed'] {
                animation: ${slideOutToLeft} 300ms cubic-bezier(0.16, 1, 0.3, 1);
            }
        `}
    
    /* Top side */
    ${(props) =>
        props.$side === 'top' &&
        css`
            /* inset-x-0 top-0 h-auto border-b */
            left: 0;
            right: 0;
            top: 0;
            height: auto;
            border-bottom: 1px solid var(--border, #e0e0e0);

            &[data-state='open'] {
                animation: ${slideInFromTop} 500ms cubic-bezier(0.16, 1, 0.3, 1);
            }

            &[data-state='closed'] {
                animation: ${slideOutToTop} 300ms cubic-bezier(0.16, 1, 0.3, 1);
            }
        `}
    
    /* Bottom side */
    ${(props) =>
        props.$side === 'bottom' &&
        css`
            /* inset-x-0 bottom-0 h-auto border-t */
            left: 0;
            right: 0;
            bottom: 0;
            height: auto;
            border-top: 1px solid var(--border, #e0e0e0);

            &[data-state='open'] {
                animation: ${slideInFromBottom} 500ms cubic-bezier(0.16, 1, 0.3, 1);
            }

            &[data-state='closed'] {
                animation: ${slideOutToBottom} 300ms cubic-bezier(0.16, 1, 0.3, 1);
            }
        `}
`

export const StyledCloseButton = styled(SheetPrimitive.Close)`
    /* ring-offset-background focus:ring-ring data-[state=open]:bg-secondary absolute top-4 right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none */
    position: absolute;
    top: 1rem;
    right: 1rem;
    border-radius: 0.125rem;
    opacity: 0.7;
    transition: opacity 150ms ease;

    &:hover {
        opacity: 1;
    }

    &:focus {
        outline: none;
        box-shadow:
            0 0 0 2px var(--background, #ffffff),
            0 0 0 4px var(--ring, #5e4ae3);
    }

    &:disabled {
        pointer-events: none;
    }

    &[data-state='open'] {
        background-color: var(--secondary, #f3f4f6);
    }
`

export const StyledHeader = styled.div`
    /* flex flex-col gap-1.5 p-4 */
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
    padding: 1rem;
`

export const StyledFooter = styled.div`
    /* mt-auto flex flex-col gap-2 p-4 */
    margin-top: auto;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 1rem;
`

export const StyledTitle = styled(SheetPrimitive.Title)`
    /* text-foreground font-semibold */
    color: var(--foreground, #1f2937);
    /* font-weight: 600; */
    ${getFontStyle('h3Bold')}/* 20px, 700 weight - matches font-semibold */
`

export const StyledDescription = styled(SheetPrimitive.Description)`
    /* text-muted-foreground text-sm */
    color: var(--muted-foreground, #7a7a7a);
    /* font-size: 0.875rem; */
    ${getFontStyle('p02Regular')}/* 14px, 400 weight - matches 0.875rem */
`

export const StyledCloseIcon = styled(XIcon)`
    width: 1rem;
    height: 1rem;
`

export const ScreenReaderText = styled.span`
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border-width: 0;
`
